import React, { useEffect, useState } from 'react';
import { Box, Flex, TextField } from '@radix-ui/themes';
import * as Label from '@radix-ui/react-label';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';

import { Footer } from '../../../layout/footer/Footer';
import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { RootState } from '../../../store/store';
import { setTransportationForm } from '../../../store/slices/transportation/transportationSlice';
import { getTranslationByPath } from '../../../utils/translations';
import { addNotification } from '../../../store/slices/notification/notificationSlice';
import { MainContent } from '../../../layout/content/MainContent';
import { checkAuthCode } from '../../../apollo/mutations/checkAuthCode';

import './style.css'
import { KeyboardWrapper } from '../../../components/keyboard/KeyboardWrapper';
import { getProducts } from '../../../apollo/queries/getProducts';

export type AnnouncementFormTemplateType = {
  transitionUrl: TransitionUrl
  registrationNumberInputLabel: TranslationPath
  registrationNumberInputPlaceholder: TranslationPath
  invalidRegistrationNumberText: TranslationPath
  trailerRegistrationNumberInputLabel: TranslationPath
  trailerRegistrationNumberInputPlaceholder: TranslationPath
  invalidTrailerRegistrationNumberText: TranslationPath
  trailerTypeInputLabel: TranslationPath
  trailerTypeInputTank: TranslationPath
  trailerTypeInputLoadingArea: TranslationPath
  transportationTypeInputLabel: TranslationPath
  transportationTypeInputPlaceholder: TranslationPath
  driverNameInputLabel: TranslationPath
  driverNameInputPlaceholder: TranslationPath
  phoneNumberInputLabel: TranslationPath
  phoneNumberInputPlaceholder: TranslationPath
  invalidDriverNameText: TranslationPath
  invalidProductText: TranslationPath
  pinInputLabel: TranslationPath
  pinInputPlaceholder: TranslationPath
  scheduleFromInputLabel: TranslationPath
  invalidAuthCodeText: TranslationPath
  registrationFailedText: TranslationPath
}

type Props = {
  page: Page<AnnouncementFormTemplateType>
}

type SelectBoxProps = {
  value: string,
  setValue: (value: string) => void
  label: string,
  options: { id: string, name: string }[]
}

const formatDateTime = (value: string | undefined | null): string => {
  if (value == null || value === '') return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${day}.${month}.${year} ${hours}:${minutes}`
}

const formatScheduleRange = (from: string | undefined | null, to: string | undefined | null): string => {
  const fromStr = formatDateTime(from)
  const toStr = formatDateTime(to)
  if (!fromStr && !toStr) return ''
  if (!fromStr) return toStr
  if (!toStr) return fromStr
  return `${fromStr} - ${toStr}`
}

const SelectBox = (props: SelectBoxProps) => (
  <Select.Root value={props.value} onValueChange={props.setValue}>
    <Select.Trigger className="select">
      <Select.Value placeholder={props.label} />
      <Select.Icon className="select-icon">
        <ChevronDownIcon />
      </Select.Icon>
    </Select.Trigger>
    <Select.Portal>
      <Select.Content
        role="dialog"
        position="popper"
        className="popover"
        sideOffset={4}
      >
        <Select.Viewport className="SelectViewport">
          {props.options.map(option => (
            <Select.Item key={option.id} value={option.id} className="item">
              <Select.ItemText>{option.name}</Select.ItemText>
              <Select.ItemIndicator className="item-indicator">
                <CheckIcon />
              </Select.ItemIndicator>
            </Select.Item>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Portal>
  </Select.Root>
);

export const AnnouncementFormTemplate = (props: Props): React.ReactElement => {
  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)
  const requiresProductSelect = !!fetchedTransportation?.requiresProductSelect

  const language = useLanguage();
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [productOptions, setProductOptions] = useState<{ id: string, name: string }[]>([])

  useEffect(() => {
    const init = async () => {
      dispatch(setTransportationForm({
        ...transportationForm,
        pin: '',
        productId: '',
        productName: '',
      }))

      if (!requiresProductSelect) {
        setProductOptions([])
        setIsLoading(false)
        return
      }

      try {
        const response = await getProducts()
        const options = (response.data?.getProducts ?? []).map((option) => ({
          id: String(option.id),
          name: option.name,
        }))
        setProductOptions(options)
      } catch (e) {
        console.log(e)
      }
      setIsLoading(false)
    }
    init()
  }, [requiresProductSelect]);
  
  const checkAuthCodeFn = async () => {
    let data;
    if (fetchedTransportation === null) {
      return
    } else {
      data = {
        authCode: transportationForm.pin,
        phoneNumber: transportationForm.phoneNumber,
      }
    }

    try {
      setIsSaving(true)
      const result = await checkAuthCode(data)
      if (result.data?.checkAuthCode) {
        navigate(props.page.data.transitionUrl, { replace: true }) 
      } else {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.registrationFailedText) },
        ))
      }
    } catch (e) {
      console.log(e)
      dispatch( addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.registrationFailedText) },
      ))
    }
    setIsSaving(false)
  }

  const onContinue = () => {
    if (transportationForm.pin.length === 0) {
      dispatch( addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidAuthCodeText) },
      ))
      return
    }

    if (fetchedTransportation === null) {
      if (transportationForm.registrationNumber.length === 0) {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidRegistrationNumberText) },
        ))
        return
      }
      if (transportationForm.driverName.length === 0) {
        dispatch( addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidDriverNameText) },
        ))
        return
      }
    }
    if (requiresProductSelect && !transportationForm.productId) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidProductText) },
      ))
      return
    }

    checkAuthCodeFn()
  }

  if (isLoading) {
    return <></>
  }

  return (
    <>
      <MainContent>
        <Flex className="flex-col justify-center items-center w-full mt-10">
          <Flex direction="column" gap="1" width="100%" className="items-center">
            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.driverNameInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.driverNameInputLabel)}>
                <TextField.Root
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.driverNameInputPlaceholder)}
                  value={transportationForm.driverName}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      driverName: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>


            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.phoneNumberInputLabel)}
              </Label.Root>
              <KeyboardWrapper layout="numeric" title={getTranslationByPath(language.translations, props.page.data.phoneNumberInputLabel)}>
                <TextField.Root
                  disabled={fetchedTransportation !== null}
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.phoneNumberInputPlaceholder)}
                  value={transportationForm.phoneNumber}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      registrationNumber: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.pinInputLabel)}
              </Label.Root>
              <KeyboardWrapper layout="numeric" title={getTranslationByPath(language.translations, props.page.data.pinInputLabel)}>
                <TextField.Root
                  type="number"
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.pinInputPlaceholder)}
                  value={transportationForm.pin}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      pin: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.registrationNumberInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.registrationNumberInputLabel)}>
                <TextField.Root
                  disabled
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.registrationNumberInputPlaceholder)}
                  value={transportationForm.registrationNumber}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      registrationNumber: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>

            {requiresProductSelect && (
              <Box maxWidth="500px" className="w-full mt-5">
                <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                  {getTranslationByPath(language.translations, props.page.data.transportationTypeInputLabel)}
                </Label.Root>
                <SelectBox
                  value={transportationForm.productId ? String(transportationForm.productId) : ''}
                  setValue={(value) => {
                    const selectedOption = productOptions.find(option => option.id === value)
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      productId: Number(value),
                      productName: selectedOption?.name ?? '',
                    }))
                  }}
                  label={getTranslationByPath(language.translations, props.page.data.transportationTypeInputPlaceholder)}
                  options={productOptions}
                />
              </Box>
            )}

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.scheduleFromInputLabel)}
              </Label.Root>
              <TextField.Root
                disabled
                style={{ height: '50px' }}
                className="mt-3"
                value={formatScheduleRange(transportationForm.scheduleFrom, transportationForm.scheduleTo)}
              />
            </Box>
          </Flex>
        </Flex>
      </MainContent>

      <Footer backUrl={props.page.footer?.back} onContinue={onContinue} disabledContinueBtn={isSaving} />
    </>
  )
}
